import type { SupabaseClient } from "@supabase/supabase-js";

import {
  fetchCommercialReadiness,
  summarizeCommercialReadiness,
  type BillingMode,
  type CommercialEntitlementStatus,
  type CommercialReadinessData,
} from "../data/commercial-admin";
import { humanizeLabel } from "../data/alert-queue";
import { useReadModel } from "../data/useReadModel";

// Commercial readiness (Admin) — read-only visibility over existing Task 59a/59b foundations:
// commercial context, entitlement inventory, and usage counter inventory. No Stripe, package
// writes, brand writes, report writes, or relationship ids are exposed here.

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function billingLabel(mode: BillingMode): string {
  if (mode === "self") return "This workspace";
  if (mode === "external") return "External account";
  return "Not configured";
}

function statusTone(status: CommercialEntitlementStatus): string {
  if (status === "active" || status === "trialing") return "ok";
  if (status === "expired") return "warn";
  return "muted";
}

function formatLimit(value: number | null): string {
  return value === null ? "Unlimited" : value.toLocaleString();
}

function buildLanes(
  data: CommercialReadinessData,
  canManageBilling: boolean,
  canManageBrand: boolean,
): Array<{ label: string; value: string; tone: "ok" | "warn" | "muted" }> {
  const summary = summarizeCommercialReadiness(data);
  return [
    {
      label: "Billing admin",
      value: canManageBilling ? "Available" : "No access",
      tone: canManageBilling ? "ok" : "muted",
    },
    {
      label: "Brand admin",
      value: canManageBrand ? "Available" : "No access",
      tone: canManageBrand ? "ok" : "muted",
    },
    {
      label: "White-label profile",
      value: summary.hasBrandProfile ? "Configured" : "Not configured",
      tone: summary.hasBrandProfile ? "ok" : "warn",
    },
    {
      label: "Billing integration",
      value: "Not wired",
      tone: "muted",
    },
    {
      label: "Commercial reports",
      value: "Not wired",
      tone: "muted",
    },
  ];
}

export function CommercialAdminReadiness({
  client,
  accountId,
  pollMs = 30000,
  canManageBilling = false,
  canManageBrand = false,
}: {
  client: SupabaseClient;
  accountId: string;
  pollMs?: number;
  canManageBilling?: boolean;
  canManageBrand?: boolean;
}) {
  const { status, data, error } = useReadModel<CommercialReadinessData>(
    () => fetchCommercialReadiness(client, accountId),
    [client, accountId],
    pollMs,
  );

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading commercial readiness…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load commercial readiness."}
      </p>
    );
  }

  const readiness = data ?? {
    context: {
      billingMode: "unknown" as BillingMode,
      operatorAccountCount: 0,
      brandDisplayName: null,
      brandKey: null,
      themeKey: null,
    },
    entitlements: [],
    usageCounters: [],
  };
  const summary = summarizeCommercialReadiness(readiness);
  const lanes = buildLanes(readiness, canManageBilling, canManageBrand);

  return (
    <div className="commercial">
      <dl className="commercial__facts" aria-label="Commercial readiness summary">
        <div className="commercial__fact">
          <dt>Billing account</dt>
          <dd>{billingLabel(readiness.context.billingMode)}</dd>
        </div>
        <div className="commercial__fact">
          <dt>Operators</dt>
          <dd>{pluralize(readiness.context.operatorAccountCount, "relationship")}</dd>
        </div>
        <div className="commercial__fact">
          <dt>Brand profile</dt>
          <dd>{readiness.context.brandDisplayName ?? readiness.context.brandKey ?? "Not configured"}</dd>
        </div>
        <div className="commercial__fact">
          <dt>Entitlements</dt>
          <dd>
            {summary.enabledEntitlementCount}/{summary.entitlementCount} active
          </dd>
        </div>
      </dl>

      <ul className="commercial__lanes" aria-label="Task 59 commercial lanes">
        {lanes.map((lane) => (
          <li key={lane.label} className="commercial__lane">
            <span>{lane.label}</span>
            <strong className={`commercial__chip commercial__chip--${lane.tone}`}>{lane.value}</strong>
          </li>
        ))}
      </ul>

      <section className="commercial__section" aria-label="Feature entitlements">
        <h3 className="commercial__subhead">Feature entitlements</h3>
        {readiness.entitlements.length === 0 ? (
          <p className="surface__state" role="status">No feature entitlements configured.</p>
        ) : (
          <table className="commercial__table">
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col">Status</th>
                <th scope="col">Limit</th>
                <th scope="col">Soft limit</th>
              </tr>
            </thead>
            <tbody>
              {readiness.entitlements.map((row) => (
                <tr key={row.featureKey}>
                  <td>{humanizeLabel(row.featureKey)}</td>
                  <td>
                    <span className={`commercial__chip commercial__chip--${statusTone(row.status)}`}>
                      {humanizeLabel(row.status)}
                    </span>
                  </td>
                  <td>{formatLimit(row.limitValue)}</td>
                  <td>{row.softLimitValue === null ? "—" : row.softLimitValue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="commercial__section" aria-label="Usage counters">
        <h3 className="commercial__subhead">Usage counters</h3>
        {readiness.usageCounters.length === 0 ? (
          <p className="surface__state" role="status">No usage counters recorded.</p>
        ) : (
          <table className="commercial__table">
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col">Usage</th>
                <th scope="col">Period</th>
              </tr>
            </thead>
            <tbody>
              {readiness.usageCounters.map((row) => (
                <tr key={`${row.featureKey}-${row.periodStart ?? "start"}-${row.periodEnd ?? "end"}`}>
                  <td>{humanizeLabel(row.featureKey)}</td>
                  <td>{row.usageCount.toLocaleString()}</td>
                  <td>
                    {row.periodStart ?? "—"} to {row.periodEnd ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
